import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingPage } from '@/components/ui/loading';
import { Badge } from '@/components/ui/badge';
import { validateInviteToken, acceptInvite } from '../api/invite-api';
import type { InviteValidation } from '../api/invite-api';
import { useAuth } from '../hooks/use-auth';
import { roleLabel } from '@/lib/display-labels';

const PASSWORD_MIN_LENGTH = 12;

function validatePassword(pw: string): string | undefined {
  if (!pw) return 'Password is required.';
  if (pw.length < PASSWORD_MIN_LENGTH) return `Must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (!/[A-Z]/.test(pw)) return 'Must contain an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Must contain a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Must contain a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain a special character.';
  return undefined;
}

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token') ?? '';

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InviteValidation | null>(null);
  const [invalidToken, setInvalidToken] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // New account form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalidToken(true);
      setLoading(false);
      return;
    }
    validateInviteToken(token)
      .then(setInvite)
      .catch(() => setInvalidToken(true))
      .finally(() => setLoading(false));
  }, [token]);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!lastName.trim()) newErrors.lastName = 'Last name is required.';
    const pwError = validatePassword(password);
    if (pwError) newErrors.password = pwError;
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password.';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // New user: create account + auto-login
  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || !invite) return;
    setSubmitError('');
    setIsSubmitting(true);
    try {
      await acceptInvite(token, { firstName, lastName, password });
      const result = await login({ email: invite.email, password });
      if (result.success) {
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Failed to accept invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // Existing user: accept invite (no password needed, backend just adds membership)
  async function handleAcceptExisting() {
    if (!invite) return;
    setSubmitError('');
    setIsSubmitting(true);
    try {
      await acceptInvite(token, { firstName: '', lastName: '', password: '' });
      setAccepted(true);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Failed to accept invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (loading) return <LoadingPage />;

  // Invalid token
  if (invalidToken || !invite) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Invalid Invitation</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              This invite link is invalid or has expired. Please ask your administrator to send a new invitation.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Existing user: accepted successfully
  if (accepted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
            <div className="w-16 h-16 rounded-full bg-success-50 dark:bg-success-500/20 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-success-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Invitation Accepted</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              You've joined <strong className="text-gray-900 dark:text-gray-100">{invite.organizationName}</strong> as a <strong className="text-gray-900 dark:text-gray-100">{roleLabel[invite.role]}</strong>.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Continue to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Existing user: confirm acceptance
  if (invite.userExists) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-sm w-full">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Join {invite.organizationName}
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                You've been invited as
              </p>
              <Badge variant="info" size="md" className="mt-2">{roleLabel[invite.role]}</Badge>
            </div>

            <div className="mb-6 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-center">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Account</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{invite.email}</p>
            </div>

            {submitError && (
              <div className="mb-4 p-3 rounded-lg bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/30">
                <p className="text-sm text-error-700 dark:text-error-400">{submitError}</p>
              </div>
            )}

            <Button onClick={handleAcceptExisting} className="w-full py-3 text-base" isLoading={isSubmitting}>
              Accept Invitation
            </Button>

            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4">
              This will add {invite.organizationName} to your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // New user: create account
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Join {invite.organizationName}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Create your account to get started
            </p>
            <Badge variant="info" size="md" className="mt-2">{roleLabel[invite.role]}</Badge>
          </div>

          <div className="mb-5 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-center">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Email</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{invite.email}</p>
          </div>

          <form noValidate onSubmit={handleCreateAccount} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                required
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setErrors(p => ({ ...p, firstName: '' })); }}
                placeholder="John"
                error={errors.firstName}
                autoFocus
              />
              <Input
                label="Last Name"
                required
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setErrors(p => ({ ...p, lastName: '' })); }}
                placeholder="Doe"
                error={errors.lastName}
              />
            </div>

            <Input
              label="Password"
              type="password"
              required
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: '' })); }}
              helperText={`At least ${PASSWORD_MIN_LENGTH} characters with uppercase, lowercase, number, and special character.`}
              error={errors.password}
            />

            <Input
              label="Confirm Password"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setErrors(p => ({ ...p, confirmPassword: '' })); }}
              error={errors.confirmPassword}
            />

            {submitError && (
              <div className="p-3 rounded-lg bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/30">
                <p className="text-sm text-error-700 dark:text-error-400">{submitError}</p>
              </div>
            )}

            <Button type="submit" className="w-full py-3 text-base" isLoading={isSubmitting}>
              Create Account & Join
            </Button>
          </form>

          <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
            Already have an account?{' '}
            <a href="/login" className="text-primary-600 dark:text-primary-400 hover:underline">Sign in</a>
          </p>
        </div>
      </div>
    </div>
  );
}
